import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import {
  OPTIONAL_PARAM_KEYS, PARAM_DEFAULT, PARAM_HELP, PARAM_LABEL, QUERY_KEYS, paramRangeNote,
  paramValid, rampValueOn,
} from './format';
import { C } from './tokens';
import { RestockV2ParamKey, RestockV2Params, RestockV2Plan } from './types';

interface SettingsDialogProps {
  open: boolean;
  plan: RestockV2Plan;
  params: Partial<RestockV2Params>;
  // The endpoint validates these too and answers in zh-TW; show its sentence verbatim rather than
  // a generic 「格式錯誤」, and pin it under the field it names when it names one.
  serverError?: string;
  onClose: () => void;
  onApply: (params: Partial<RestockV2Params>) => void;
}

type Draft = Record<RestockV2ParamKey, string>;

const blank = (): Draft => ({
  cover_days: '', lead_budget: '', new_styles_per_week: '', pieces_per_new_style: '',
  newness_days: '', ss_core_depth_cap: '', capacity: '', aw_target_pct: '',
});

// The ⚙ of R1–R6: every rule number is a field here, and nothing is persisted — the values ride on
// the GET's query string, so a bad experiment is undone by reloading the tab.
const SettingsDialog: React.FC<SettingsDialogProps> = ({ open, plan, params, serverError, onClose, onApply }) => {
  const [draft, setDraft] = useState<Draft>(blank);
  const settings = plan.racks?.settings;
  const autumn = plan.meta?.autumn;
  const rampToday = rampValueOn(autumn?.ramp, new Date());
  const awPlaceholder = rampToday ?? autumn?.target_pct ?? settings?.autumn_target_percent ?? null;

  // What the user last applied wins over what the endpoint echoed back, so an override sticks.
  useEffect(() => {
    if (!open) return;
    const read = (key: RestockV2ParamKey): string => {
      const applied = params[key];
      if (typeof applied === 'number') return String(applied);
      const echoed = plan.params?.[key];
      if (typeof echoed === 'number') return String(echoed);
      if (OPTIONAL_PARAM_KEYS.indexOf(key) >= 0) return '';
      return String(PARAM_DEFAULT[key]);
    };
    const next = blank();
    QUERY_KEYS.forEach((key) => { next[key] = read(key); });
    setDraft(next);
  }, [open, plan.params, params]);

  const valid = QUERY_KEYS.every((key) => paramValid(key, draft[key]));
  // The server names the offending setting either by its zh-TW label or by its query-string key.
  const fieldError = (key: RestockV2ParamKey): string => {
    if (!serverError) return '';
    return serverError.indexOf(PARAM_LABEL[key]) >= 0 || serverError.indexOf(key) >= 0 ? serverError : '';
  };

  const apply = () => {
    const next: Partial<RestockV2Params> = {};
    QUERY_KEYS.forEach((key) => {
      const text = draft[key].trim();
      if (text !== '') next[key] = Number(text);
    });
    onClose();
    onApply(next);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>調整計算設定</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {!!serverError && <Alert severity="error">{serverError}</Alert>}
        <Box
          sx={{
            bgcolor: C.waitWash, border: '1px solid', borderColor: C.wait, borderRadius: '10px',
            px: 1.25, py: 1,
          }}
        >
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: C.wait }}>
            今天的秋款目標 {awPlaceholder ?? '—'}%（照日曆表算）
          </Typography>
          {!!(autumn?.ramp || []).length && (
            <Typography sx={{ fontSize: 11, color: C.ink2, mt: 0.25 }}>
              {(autumn?.ramp || []).map((point) => `${point.date} ${point.pct}%`).join(' · ')}
            </Typography>
          )}
        </Box>
        {QUERY_KEYS.map((key) => (
          <TextField
            key={key} size="small" label={PARAM_LABEL[key]} value={draft[key]}
            onChange={(event) => setDraft((old) => ({ ...old, [key]: event.target.value }))}
            inputProps={{ inputMode: 'numeric' }}
            placeholder={key === 'aw_target_pct' && awPlaceholder !== null ? String(awPlaceholder) : undefined}
            error={!paramValid(key, draft[key]) || !!fieldError(key)}
            helperText={fieldError(key) || (paramValid(key, draft[key])
              ? (key === 'capacity' ? `留白＝衣架設定加總 ${settings?.capacity ?? 0}` : PARAM_HELP[key])
              : paramRangeNote(key))}
          />
        ))}
        <Typography sx={{ fontSize: 11.5, color: C.ink3 }}>只影響這次的計算，不會存成設定。</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" disabled={!valid} onClick={apply}>套用</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
